import { memo, useContext } from 'react'
import { Pressable, ScrollView, StyleSheet } from 'react-native'

import {
  DepositActionLayers,
  DepositActionMotion,
} from '../DepositActionMotion'

import { DEPOSIT_METRICS } from '../../screens/depositMetrics'
import { withAlpha } from '../../utils/withAlpha'
import { useHapticFeedback } from '../../haptics/HapticsContext'
import { Box, Text, useTheme } from '../../theme/restyle'

export interface QuickAmountOption {
  /** The amount the chip enters, as a decimal string (e.g. `'50'`). */
  value: string
  /** What the chip shows (e.g. `'$50'`). */
  label: string
}

export interface QuickAmountsRowProps {
  options: QuickAmountOption[]
  onSelect: (value: string) => void
  /** Makes every shortcut non-interactive. */
  disabled?: boolean
}

/**
 * The recommended-amount shortcuts shown while the deposit amount is empty.
 */
export const QuickAmountsRow = memo(function QuickAmountsRow({
  options,
  onSelect,
  disabled = false,
}: QuickAmountsRowProps) {
  const animateDeposit = useContext(DepositActionMotion)
  const theme = useTheme()
  const haptic = useHapticFeedback()
  // `detail` is the 14pt cut, but it also dims the text; the chips are primary.
  const labelColor = { color: theme.content.elements.primary }

  const overflow = options.length > 4
  const content = (
    <Box
      flexDirection="row"
      style={overflow ? [styles.row, styles.overflow] : styles.row}
    >
      {options.map(({ value, label }) => (
        <Pressable
          key={value}
          style={overflow ? styles.scrollingShortcut : styles.shortcut}
          accessibilityRole="button"
          accessibilityLabel={label}
          accessibilityState={{ disabled }}
          disabled={disabled}
          onPress={() => {
            haptic()
            onSelect(value)
          }}
        >
          {animateDeposit ? (
            <DepositActionLayers
              stateKey={value}
              style={styles.chip}
              fillColor={withAlpha(theme.content.elements.primary, 0.07)}
              radius={DEPOSIT_METRICS.chips.radius}
              contentStyle={styles.animatedContent}
            >
              <Text
                variant="detail"
                weight="medium"
                style={[styles.label, labelColor]}
                numberOfLines={1}
              >
                {label}
              </Text>
            </DepositActionLayers>
          ) : (
            <Box
              style={[
                styles.chip,
                {
                  backgroundColor: withAlpha(
                    theme.content.elements.primary,
                    0.07,
                  ),
                },
              ]}
            >
              <Text
                variant="detail"
                weight="medium"
                style={[styles.label, labelColor]}
                numberOfLines={1}
              >
                {label}
              </Text>
            </Box>
          )}
        </Pressable>
      ))}
    </Box>
  )
  return overflow ? (
    <ScrollView
      horizontal
      style={styles.viewport}
      showsHorizontalScrollIndicator
    >
      {content}
    </ScrollView>
  ) : (
    content
  )
})

const styles = StyleSheet.create({
  // The row spans the action area, so its edges are the submit button's
  // edges at every width and the shortcuts grow with the screen.
  row: {
    width: '100%',
    height: DEPOSIT_METRICS.chips.height,
    alignItems: 'center',
    gap: DEPOSIT_METRICS.chips.gap,
    paddingVertical: DEPOSIT_METRICS.chips.paddingVertical,
  },
  viewport: { width: '100%' },
  overflow: { width: 'auto' },
  // The amount above sets the screen's figure style, so the chips take it too —
  // otherwise their digits read as a different face next to it.
  label: {
    letterSpacing: DEPOSIT_METRICS.chips.tracking,
    fontVariant: ['tabular-nums'],
  },
  animatedContent: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  // Sharing the row means sharing its width; a scrolling row keeps Figma's.
  shortcut: { flex: 1 },
  scrollingShortcut: { width: DEPOSIT_METRICS.chips.chipWidth },
  chip: {
    width: '100%',
    height: DEPOSIT_METRICS.chips.chipHeight,
    borderRadius: DEPOSIT_METRICS.chips.radius,
    alignItems: 'center',
    justifyContent: 'center',
  },
})
